import { ExternalLink, CheckCircle2, XCircle } from 'lucide-react'
import { GRADE_COLORS } from '@/lib/radar/scoring'
import { sanitizeHref } from '@/lib/sanitize'
import { MiniStat } from './MiniStat'
import { ScoringMethodologyNote } from '@/components/radar/ScoringMethodologyNote'
import type { ToolDetailProps } from './detailRegistry'

interface SchemaAuditViewData {
  grade: string
  overallScore: number
  url?: string
  scoreBreakdown?: {
    category: string
    score: number
    maxScore: number
    details?: string
    color?: string
  }[]
  schemaTypes?: {
    type: string
    found: boolean
    evaluated?: boolean
    completeness?: number
  }[]
  pagesChecked?: number
  hasSpeakable?: boolean
  recommendations?: { priority: string; message: string }[]
}

/**
 * Dashboard detail renderer for `schemaAudit` (Phase 3, Step 1 — Class D,
 * dashboard-only). Faithfully reproduces the legacy ToolDetailPanel rendering:
 * header (icon + name + grade pill + url), score-breakdown bars, the
 * Pages/Speakable MiniStats + Schema Types grid, and recommendations.
 *
 * schemaAudit ships `aiSummary: null`, so the AI Analysis block is omitted (it
 * never rendered for this tool). Grade comes from `data.grade` — never
 * recomputed. Render-layer only.
 */
export function SchemaAuditDetail({
  data,
  toolIcon: Icon,
  toolColor,
  toolName,
}: ToolDetailProps) {
  const d = data as unknown as SchemaAuditViewData

  return (
    <div
      id='tool-detail-panel'
      className='rounded-xl border border-border bg-card p-5 space-y-5'
    >
      {/* Header */}
      <div className='flex items-baseline gap-3'>
        <span className='relative top-[3px] flex-shrink-0'>
          <Icon size={22} style={{ color: toolColor }} />
        </span>
        <h3 className='text-base font-bold text-foreground leading-none'>
          {toolName}
        </h3>
        <span
          className='text-sm font-bold px-2.5 py-1 rounded leading-none'
          style={{
            // color-mix, not a `+15` hex-alpha suffix: GRADE_COLORS now
            // returns theme-aware `var(--pm-grade-*)` strings.
            background: `color-mix(in srgb, ${GRADE_COLORS[d.grade]} 8%, transparent)`,
            color: GRADE_COLORS[d.grade],
          }}
        >
          {d.overallScore}/100
        </span>
        {d.url &&
          (() => {
            const safeUrl = sanitizeHref(d.url, {
              allowedSchemes: ['https:', 'http:'],
            })
            if (!safeUrl) return null
            return (
              <a
                href={safeUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='ml-auto flex items-center gap-1 text-xs hover:underline text-muted-foreground'
              >
                {d.url} <ExternalLink size={9} />
              </a>
            )
          })()}
      </div>

      <ScoringMethodologyNote toolSlug='schema-audit' />

      {/* Score breakdown */}
      {d.scoreBreakdown && d.scoreBreakdown.length > 0 && (
        <div>
          <h4 className='text-xs font-bold mb-4 uppercase tracking-wide text-muted-foreground'>
            Score Breakdown
          </h4>
          <div className='space-y-4'>
            {d.scoreBreakdown.map(cat => (
              <div key={cat.category}>
                <div className='flex justify-between text-sm mb-1.5'>
                  <span className='text-foreground/90 font-medium'>
                    {cat.category}
                  </span>
                  <span className='font-mono font-bold text-foreground'>
                    {cat.score}/{cat.maxScore}
                  </span>
                </div>
                <div className='h-2 rounded-full bg-muted'>
                  <div
                    className='h-full rounded-full transition-all duration-500'
                    style={{
                      width: `${cat.maxScore > 0 ? (cat.score / cat.maxScore) * 100 : 0}%`,
                      background: cat.color || toolColor,
                    }}
                  />
                </div>
                {cat.details && (
                  <p className='text-xs mt-1.5 text-muted-foreground leading-relaxed'>
                    {cat.details}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Schema Audit: type coverage grid */}
      {d.schemaTypes && (
        <div>
          <div className='grid grid-cols-2 gap-3 mb-4'>
            <MiniStat label='Pages Checked' value={d.pagesChecked || 0} />
            <MiniStat
              label='Speakable'
              value={d.hasSpeakable ? 1 : 0}
              color={d.hasSpeakable ? '#22c55e' : '#F90B8A'}
            />
          </div>
          <h4 className='text-xs font-bold mb-3 uppercase tracking-wide text-muted-foreground'>
            Schema Types ({d.schemaTypes.length})
          </h4>
          <div className='grid grid-cols-2 md:grid-cols-3 gap-2'>
            {d.schemaTypes.map(t => (
              <div
                key={t.type}
                className='flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2'
              >
                {t.found ? (
                  <CheckCircle2 size={14} style={{ color: '#22c55e' }} />
                ) : (
                  <XCircle size={14} style={{ color: '#F90B8A' }} />
                )}
                <span className='text-sm truncate text-foreground/90'>
                  {t.type}
                </span>
                {t.found && t.evaluated !== false && (
                  <span className='ml-auto text-xs font-mono font-bold text-muted-foreground'>
                    {t.completeness}%
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {d.recommendations && d.recommendations.length > 0 && (
        <div>
          <h4 className='text-xs font-bold mb-3 uppercase tracking-wide text-muted-foreground'>
            Recommendations ({d.recommendations.length})
          </h4>
          <div className='space-y-2.5'>
            {d.recommendations.map((rec, i) => (
              <div
                key={i}
                className='flex items-start gap-3 text-sm leading-relaxed'
              >
                <span
                  className='w-2 h-2 rounded-full mt-1.5 flex-shrink-0'
                  style={{
                    background:
                      rec.priority === 'high'
                        ? '#F90B8A'
                        : rec.priority === 'medium'
                          ? 'var(--warning-text)'
                          : '#22c55e',
                  }}
                />
                <span className='text-foreground/85'>{rec.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
